// BackupAndRestore.tsx - Enhanced Version
import React, { useEffect, useRef, useState } from "react";
import { Application } from "@microsoft/microsoft-graph-types";
import {
  Box,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Typography,
  Button,
  Stack,
  Divider,
  Card,
  CardContent,
  Alert,
  Paper,
  Tooltip,
  Chip,
  IconButton
} from "@mui/material";

// Icons for enhanced visual design
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import BackupIcon from "@mui/icons-material/Backup";
import RestoreIcon from "@mui/icons-material/Restore";
import SaveIcon from "@mui/icons-material/Save";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import StorageIcon from "@mui/icons-material/Storage";
import DeleteSweepIcon from "@mui/icons-material/DeleteSweep";
import DeleteForeverIcon from "@mui/icons-material/DeleteForever";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import AssessmentIcon from "@mui/icons-material/Assessment";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import CloudDownloadIcon from "@mui/icons-material/CloudDownload";
import ListIcon from "@mui/icons-material/List";

/**
 * Props:
 *  - app: The current Application object
 *  - updateApplication: your callback to patch changes to Entra ID
 *  - onBackupJsonLoaded: a callback that sets `modifiedApp` in AppTester
 */
interface BackupAndRestoreProps {
  app: Application;
  updateApplication: () => void;
  onBackupJsonLoaded?: (parsed: any) => void;
}

const BackupAndRestore: React.FC<BackupAndRestoreProps> = ({
  app,
  updateApplication,
  onBackupJsonLoaded
}) => {
  // Update state to have section-specific alerts
  const [actionFeedback, setActionFeedback] = useState<{
    show: boolean;
    message: string;
    severity: 'success' | 'error' | 'warning' | 'info';
    section: 'manual' | 'storage' | 'global'; // Which section should display this alert
  }>({
    show: false,
    message: '',
    severity: 'info',
    section: 'global'
  });
  
  // A hidden file input for restoring from file
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  // Update the showFeedback helper to include section
  const showFeedback = (
    message: string, 
    severity: 'success' | 'error' | 'warning' | 'info',
    section: 'manual' | 'storage' | 'global' = 'global'
  ) => {
    setActionFeedback({
      show: true,
      message,
      severity,
      section
    });
  
  // Auto-dismiss after 5 seconds
  setTimeout(() => {
    setActionFeedback(prev => ({ ...prev, show: false }));
  }, 5000);
};

  /**
   * Silent Backup History Helpers (per-app, rolling last 3)
   */
  const MAX_HISTORY = 3;
  const buildIndexKey = (appId: string) => `backupIndex-${appId}`;
  const buildItemKey = (appId: string, isoTs: string) => `backup-${appId}-${isoTs}`; // manual backups
  // Silent-only index references (points to same item keys)
  const buildSilentIndexKey = (appId: string) => `silentBackupIndex-${appId}`;
  const buildSilentItemKey = (appId: string, isoTs: string) => `silentBackup-${appId}-${isoTs}`;

  const loadIndex = (appId?: string): string[] => {
    if (!appId) return [];
    const raw = localStorage.getItem(buildIndexKey(appId));
    if (!raw) return [];
    try {
      const parsed = JSON.parse(raw);
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  };

  const saveIndex = (appId?: string, index: string[] = []) => {
    if (!appId) return;
    localStorage.setItem(buildIndexKey(appId), JSON.stringify(index));
  };

  const getRecentBackups = (): { ts: string; dateLabel: string }[] => {
    const idx = loadIndex(app.id || undefined);
    return idx.slice(0, MAX_HISTORY).map(ts => ({ ts, dateLabel: new Date(ts).toLocaleString() }));
  };

  const loadSilentIndex = (appId?: string): string[] => {
    if (!appId) return [];
    const raw = localStorage.getItem(buildSilentIndexKey(appId));
    if (!raw) return [];
    try {
      const parsed = JSON.parse(raw);
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  };

  const saveSilentIndex = (appId?: string, index: string[] = []) => {
    if (!appId) return;
    localStorage.setItem(buildSilentIndexKey(appId), JSON.stringify(index));
  };

  const getRecentSilentBackups = (): { ts: string; dateLabel: string }[] => {
    const appId = app.id || undefined;
    const idx = loadSilentIndex(appId);
    const exists = (ts: string) => {
      if (!appId) return false;
      // Check silent key first, then generic as fallback for older entries
      return (
        !!localStorage.getItem(buildSilentItemKey(appId, ts)) ||
        !!localStorage.getItem(buildItemKey(appId, ts))
      );
    };
    return idx
      .filter(ts => exists(ts))
      .slice(0, MAX_HISTORY)
      .map(ts => ({ ts, dateLabel: new Date(ts).toLocaleString() }));
  };

  const downloadBackupByTimestamp = (ts: string) => {
    if (!app.id) {
      showFeedback("Cannot determine app id for backup.", 'error', 'storage');
      return;
    }
    const data = localStorage.getItem(buildItemKey(app.id, ts));
    if (!data) {
      showFeedback("Selected backup not found in browser storage.", 'warning', 'storage');
      return;
    }
    const blob = new Blob([data], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const safeName = app.displayName?.replace(/\s+/g, "_") || app.id;
    const safeTs = ts.replace(/[:.]/g, "-");
    const link = document.createElement('a');
    link.href = url;
    link.download = `Backup_${safeName}_${safeTs}.json`;
    link.click();
    URL.revokeObjectURL(url);
    showFeedback("Backup downloaded successfully!", 'success', 'storage');
  };

  const downloadSilentBackupByTimestamp = (ts: string) => {
    if (!app.id) {
      showFeedback("Cannot determine app id for backup.", 'error', 'storage');
      return;
    }
    // Prefer silent backup item; fallback to generic backup item for legacy entries
    let data = localStorage.getItem(buildSilentItemKey(app.id, ts));
    if (!data) {
      data = localStorage.getItem(buildItemKey(app.id, ts));
    }
    if (!data) {
      showFeedback("Selected silent backup not found in browser storage.", 'warning', 'storage');
      return;
    }
    const blob = new Blob([data], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const safeName = app.displayName?.replace(/\s+/g, "_") || app.id;
    const safeTs = ts.replace(/[:.]/g, "-");
    const link = document.createElement('a');
    link.href = url;
    link.download = `SilentBackup_${safeName}_${safeTs}.json`;
    link.click();
    URL.revokeObjectURL(url);
    showFeedback("Silent backup downloaded successfully!", 'success', 'storage');
  };

  /***********************************************************************
   *  1) MANUAL BACKUP: export as JSON file + store in localStorage
   ***********************************************************************/
  const handleBackupNow = () => {
    try {
      const jsonData = JSON.stringify(app, null, 2);
      const blob = new Blob([jsonData], { type: "application/json" });
      const url = URL.createObjectURL(blob);

      // Trigger file download
      const link = document.createElement("a");
      link.href = url;
      link.download = `Backup_${app.displayName?.replace(/\s+/g, "_")}_${new Date().toISOString()}.json`;
      link.click();
      URL.revokeObjectURL(url);

      // Also store in localStorage with timestamp
      const nowIso = new Date().toISOString();

      // New: append to per-app rolling history (keep last 3)
      if (app.id) {
        // Store full JSON under a timestamped key
        const itemKey = buildItemKey(app.id, nowIso);
        localStorage.setItem(itemKey, jsonData);

        // Maintain index (most-recent first)
        let index = loadIndex(app.id);
        // Prepend and make unique
        index = [nowIso, ...index.filter(ts => ts !== nowIso)];
        // Sort descending by ISO timestamp (lexicographic works for ISO)
        index.sort((a, b) => b.localeCompare(a));
        // Prune extras beyond MAX_HISTORY
        if (index.length > MAX_HISTORY) {
          const extras = index.slice(MAX_HISTORY);
          extras.forEach(ts => localStorage.removeItem(buildItemKey(app.id!, ts)));
          index = index.slice(0, MAX_HISTORY);
        }
        saveIndex(app.id, index);

        // Backward compatibility: keep latest at legacy keys used elsewhere
        localStorage.setItem(`backup-${app.id}`, jsonData);
        localStorage.setItem(`backupTimestamp-${app.id}`, nowIso);
      }
      
      showFeedback("Backup created successfully! File downloaded and saved in browser storage.", 'success', 'manual');
    } catch (err) {
      console.error("Backup failed:", err);
      showFeedback("Backup failed. Check console for details.", 'error', 'manual');
    }
  };

  /***********************************************************************
   *  2) RESTORE FROM FILE
   ***********************************************************************/
  const handleRestoreFromFile = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click(); // shows file picker
    }
  };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!e.target.files?.length) return;
    const file = e.target.files[0];

    try {
      const text = await file.text();
      const backupData = JSON.parse(text);

      // Immediately pass the loaded JSON up to the parent
      if (onBackupJsonLoaded) {
        onBackupJsonLoaded(backupData);
      }

      showFeedback(`Backup "${file.name}" loaded successfully. Click 'Apply Changes' to save to Entra ID.`, 'info', 'manual');
    } catch (err) {
      console.error("Error reading backup file:", err);
      showFeedback("Failed to parse backup file. Check console for details.", 'error', 'manual');
    }
  };

  /***********************************************************************
   *  3) SILENT BACKUP / LOCALSTORAGE
   ***********************************************************************/
  const lastSilentBackupIso = localStorage.getItem(`backupTimestamp-${app.id}`) || "";
  const lastSilentBackupTime = lastSilentBackupIso
    ? new Date(lastSilentBackupIso).toLocaleString()
    : null;

  const handleDownloadSilentBackup = () => {
    // Prefer silent history index (new behavior)
    const idx = loadSilentIndex(app.id || undefined);
    if (idx.length && app.id) {
      downloadSilentBackupByTimestamp(idx[0]);
      return;
    }
    // Fallback to legacy single backup key
    const key = `backup-${app.id}`;
    const data = key ? localStorage.getItem(key) : null;
    if (!data) {
      showFeedback("No silent backup found for this app.", 'warning', 'storage');
      return;
    }
    const blob = new Blob([data], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `Backup_${app.displayName?.replace(/\s+/g, "_")}_${new Date().toISOString().replace(/[:.]/g, '-')}.json`;
    link.click();
    URL.revokeObjectURL(url);
    showFeedback("Silent backup downloaded successfully!", 'success', 'storage');
  };

  /**
   * Create a silent snapshot on first mount per session per app.
   * This captures the pre-change state so users can restore even if they forget manual backup.
   */
  useEffect(() => {
    if (!app?.id) return;
    // Always capture on mount to ensure latest pre-change state is preserved.
    try {
      const jsonData = JSON.stringify(app, null, 2);
      const nowIso = new Date().toISOString();

      // Store full JSON under silent-only item key
      const sItemKey = buildSilentItemKey(app.id, nowIso);
      localStorage.setItem(sItemKey, jsonData);

      // Update silent-only index (most-recent first) and prune silent items only
      let sIndex = loadSilentIndex(app.id);
      sIndex = [nowIso, ...sIndex.filter(ts => ts !== nowIso)];
      sIndex.sort((a, b) => b.localeCompare(a));
      if (sIndex.length > MAX_HISTORY) {
        const extras = sIndex.slice(MAX_HISTORY);
        extras.forEach(ts => localStorage.removeItem(buildSilentItemKey(app.id!, ts)));
        sIndex = sIndex.slice(0, MAX_HISTORY);
      }
      saveSilentIndex(app.id, sIndex);

      // Update legacy latest keys
      localStorage.setItem(`backup-${app.id}`, jsonData);
      localStorage.setItem(`backupTimestamp-${app.id}`, nowIso);
      // Optional user feedback suppressed to keep it silent
    } catch (e) {
      // Silent failure; optional console log
      // console.warn('Silent snapshot failed', e);
    }
  // We only want to run once per session per app id
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [app?.id]);

  /***********************************************************************
   *  SHOWING / SORTING localStorage BACKUPS -> limited to last 10
   ***********************************************************************/
  const listStoredBackups = () => {
    const backupKeys = Object.keys(localStorage).filter((k) => k.startsWith("backup-"));
    if (!backupKeys.length) {
      showFeedback("No backups found in browser storage.", 'info', 'storage');
      return;
    }

    // Build an array of (backupKey, isoTimestamp)
    const backups = backupKeys.map((k) => {
      const appId = k.replace("backup-", "");
      const iso = localStorage.getItem(`backupTimestamp-${appId}`) || "";
      return { key: k, iso, appId };
    });

    // Sort descending by timestamp (newest first)
    backups.sort((a, b) => new Date(b.iso).getTime() - new Date(a.iso).getTime());

    // Show only up to the last 10
    const top10 = backups.slice(0, 10);
    
    const formattedBackups = top10.map((backup, idx) => {
      const date = new Date(backup.iso).toLocaleString();
      const appName = backup.appId === app.id ? app.displayName : backup.appId;
      return `${idx + 1}) ${appName}\n   Date: ${date}`;
    }).join('\n\n');
    
    // Use the custom notification instead of alert
    showFeedback(`Found ${backups.length} backups in browser storage. Showing newest 10:\n\n${formattedBackups}`, 'info', 'storage');
  };

  /***********************************************************************
   *  MISC - check localStorage usage
   ***********************************************************************/
  function getLocalStorageUsageKB(): number {
    let totalBytes = 0;
    for (const key in localStorage) {
      if (Object.prototype.hasOwnProperty.call(localStorage, key)) {
        const val = localStorage.getItem(key);
        if (val) totalBytes += val.length * 2; // ~2 bytes/char
      }
    }
    return totalBytes / 1024;
  }

  const handleCheckStorageHealth = () => {
    const usedKB = getLocalStorageUsageKB();
    showFeedback(`Browser storage usage: ${usedKB.toFixed(2)} KB of ~5,000 KB available`, 'info', 'storage');
  };

  /***********************************************************************
   *  PURGE OLDEST BACKUP (and show its timestamp)
   ***********************************************************************/
  const handlePurgeOldestBackup = () => {
    const backupKeys = Object.keys(localStorage).filter((k) => k.startsWith("backup-"));
    if (!backupKeys.length) {
      showFeedback("No backups found to purge.", 'warning', 'storage');
      return;
    }

    // Sort ascending => oldest first
    backupKeys.sort((a, b) => {
      const aId = a.replace("backup-", "");
      const bId = b.replace("backup-", "");
      const aIso = localStorage.getItem(`backupTimestamp-${aId}`) || "";
      const bIso = localStorage.getItem(`backupTimestamp-${bId}`) || "";
      return new Date(aIso).getTime() - new Date(bIso).getTime();
    });

    const oldestKey = backupKeys[0];
    const oldestAppId = oldestKey.replace("backup-", "");
    const oldestTime = localStorage.getItem(`backupTimestamp-${oldestAppId}`);

    localStorage.removeItem(oldestKey);
    localStorage.removeItem(`backupTimestamp-${oldestAppId}`);

    showFeedback(`Purged oldest backup from ${oldestTime || "unknown date"}`, 'success', 'storage');
  };

  /***********************************************************************
   *  PURGE ALL BACKUPS
   ***********************************************************************/
  const handlePurgeAllBackups = () => {
    const backupKeys = Object.keys(localStorage).filter((k) => k.startsWith("backup-"));
    if (!backupKeys.length) {
      showFeedback("No backups found to purge.", 'warning', 'storage');
      return;
    }

    const confirmed = window.confirm(
      "Are you sure you want to remove ALL backups? This cannot be undone!"
    );
    if (!confirmed) return;

    backupKeys.forEach((k) => {
      const appId = k.replace("backup-", "");
      localStorage.removeItem(k);
      localStorage.removeItem(`backupTimestamp-${appId}`);
    });

    showFeedback(`All ${backupKeys.length} backups have been removed from browser storage.`, 'success', 'storage');
  };

  /***********************************************************************
   *  RENDER
   ***********************************************************************/
  return (
    <Card variant="outlined" sx={{ mb: 4 }}>
      <CardContent
        sx={{
          background: 'linear-gradient(135deg, #e8eaf6 0%, #f3e5f5 100%)',
          borderBottom: '1px solid #c5cae9',
          mb: 0,
          pb: 2,
          pt: 2,
          px: 3,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 0 }}>
          <Box
            sx={{
              width: 28,
              height: 28,
              borderRadius: '8px',
              backgroundColor: '#3f51b5',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              mr: 1.5
            }}
          >
            <BackupIcon sx={{ color: 'white', fontSize: '0.85rem' }} />
          </Box>
          <Typography
            variant="h6"
            sx={{
              fontFamily: "'Segoe UI', Roboto, 'Open Sans', sans-serif",
              fontSize: "1.2rem",
              fontWeight: 600,
              color: "#283593",
              letterSpacing: "0.01em",
              flexGrow: 1
            }}
          >
            Backup & Restore
          </Typography>
          <Chip
            size="small"
            label="Backup"
            sx={{
              backgroundColor: '#c5cae9',
              color: '#283593',
              fontWeight: 500
            }}
          />
        </Box>
      </CardContent>      
      <CardContent>        
      {/* Global alerts (shown at the top) */}
      {actionFeedback.show && actionFeedback.section === 'global' && (
        <Alert 
          severity={actionFeedback.severity}
          sx={{ 
            mb: 2,
            animation: 'fadeIn 0.3s ease-in-out',
            '& .MuiAlert-message': {
              whiteSpace: 'pre-line'
            }
          }}
          onClose={() => setActionFeedback(prev => ({ ...prev, show: false }))}
        >
          {actionFeedback.message}
        </Alert>
      )}

        {/** 1) MANUAL BACKUP & RESTORE */}
        <Accordion defaultExpanded>
          <AccordionSummary 
            expandIcon={<ExpandMoreIcon />}
            sx={{ 
              backgroundColor: 'rgba(63, 81, 181, 0.04)', // Light indigo background
              '&.Mui-expanded': {
                minHeight: 56,
                borderBottom: '1px solid #c5cae9' // Indigo border
              }
            }}
          >
            <Typography 
              variant="h6" 
              sx={{ 
                fontFamily: "'Segoe UI', Roboto, 'Open Sans', sans-serif",
                fontSize: "1.1rem",
                fontWeight: 600,
                color: "#283593", // Indigo color
                letterSpacing: "0.01em",
                display: 'flex',
                alignItems: 'center'
              }}
            >
              <CloudDownloadIcon sx={{ mr: 1, color: '#3f51b5' }} />
              Manual Backup & Restore
            </Typography>
          </AccordionSummary>
          
          <AccordionDetails sx={{ backgroundColor: "#f4f8fb", borderRadius: 1, p: 3 }}>
          {/* Manual section alerts */}
          {actionFeedback.show && actionFeedback.section === 'manual' && (
            <Alert 
              severity={actionFeedback.severity}
              sx={{ 
                mb: 3,
                animation: 'fadeIn 0.3s ease-in-out',
                '& .MuiAlert-message': {
                  whiteSpace: 'pre-line'
                }
              }}
              onClose={() => setActionFeedback(prev => ({ ...prev, show: false }))}
            >
              {actionFeedback.message}
            </Alert>
          )}

            {/* Action Buttons Section */}
            <Box sx={{ 
              display: 'flex', 
              flexWrap: 'wrap',
              gap: 2,
              mb: 2
            }}>
              <Button 
                variant="contained" 
                color="primary" 
                startIcon={<BackupIcon />}
                onClick={handleBackupNow}
                sx={{ 
                  py: 1.2,
                  px: 2,
                  borderRadius: 2,
                  boxShadow: '0 2px 4px rgba(25, 118, 210, 0.2)',
                  '&:hover': {
                    boxShadow: '0 4px 8px rgba(25, 118, 210, 0.3)'
                  }
                }}
              >
                Backup to File
              </Button>
              
              <Button 
                variant="contained" 
                startIcon={<RestoreIcon />}
                onClick={handleRestoreFromFile}
                sx={{ 
                  py: 1.2,
                  px: 2,
                  borderRadius: 2,
                  backgroundColor: '#5c6bc0',
                  '&:hover': {
                    backgroundColor: '#3f51b5'
                  }
                }}
              >
                Restore from File
              </Button>
              
              <Button 
                variant="contained" 
                color="success" 
                startIcon={<SaveIcon />}
                onClick={updateApplication}
                sx={{ 
                  py: 1.2,
                  px: 2,
                  borderRadius: 2
                }}
              >
                Apply Changes
              </Button>
            </Box>

            {/* Hidden file input */}
            <input
              ref={fileInputRef}
              type="file"
              accept=".json"
              style={{ display: "none" }}
              onChange={handleFileChange}
            />

            <Chip 
              icon={<InfoOutlinedIcon />}
              label="After loading a file, click 'Apply Changes' to save to Entra ID" 
              color="default"
              sx={{ mt: 1 }}
            />
          </AccordionDetails>
        </Accordion>

        {/** 2) SILENT BACKUP */}
        <Accordion>
          <AccordionSummary
            expandIcon={<ExpandMoreIcon />}
            sx={{
              '&.Mui-expanded': {
                minHeight: 56,
                borderBottom: '1px solid rgba(0, 0, 0, 0.12)'
              }
            }}
          >
            <Typography
              variant="h6"
              sx={{
                fontFamily: "'Segoe UI', Roboto, 'Open Sans', sans-serif",
                fontSize: "1.2rem",
                fontWeight: 600,
                color: "primary.main",
                letterSpacing: "0.01em",
                display: 'flex',
                alignItems: 'center'
              }}
            >
              <StorageIcon sx={{ mr: 1 }} />
              Browser Storage Management
            </Typography>
          </AccordionSummary>

          <AccordionDetails sx={{ backgroundColor: "#f5f5ff", borderRadius: 1, p: 3 }}>
            {/* Storage section alerts */}
            {actionFeedback.show && actionFeedback.section === 'storage' && (
              <Alert
                severity={actionFeedback.severity}
                sx={{
                  mb: 3,
                  animation: 'fadeIn 0.3s ease-in-out',
                  '& .MuiAlert-message': {
                    whiteSpace: 'pre-line'
                  }
                }}
                onClose={() => setActionFeedback(prev => ({ ...prev, show: false }))}
              >
                {actionFeedback.message}
              </Alert>
            )}

            {/* Action Buttons for Silent Backups */}
            <Box sx={{
              p: 3,
              backgroundColor: "#fafbff", // Match main theme
              borderRadius: '12px',
              border: '1px solid #e8eaf6', // Indigo border
              mb: 3
            }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <Box
                  sx={{
                    width: 24,
                    height: 24,
                    borderRadius: '6px',
                    backgroundColor: '#3f51b5',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    mr: 1.5
                  }}
                >
                  <CloudDownloadIcon sx={{ color: 'white', fontSize: '0.75rem' }} />
                </Box>
                <Typography
                  variant="subtitle1"
                  sx={{
                    fontFamily: "'Segoe UI', Roboto, 'Open Sans', sans-serif",
                    fontSize: "1.1rem",
                    fontWeight: 600,
                    color: "#283593",
                    letterSpacing: "0.01em"
                  }}
                >
                  Browser Storage Actions
                </Typography>
              </Box>

              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 3 }}>
                <Button
                  variant="outlined"
                  startIcon={<FileDownloadIcon />}
                  onClick={handleDownloadSilentBackup}
                  disabled={!lastSilentBackupTime}
                >
                  Download Latest Backup
                </Button>

                <Button
                  variant="outlined"
                  startIcon={<ListIcon />}
                  onClick={listStoredBackups}
                >
                  View All Stored Backups
                </Button>

                <Button
                  variant="outlined"
                  color="info"
                  startIcon={<AssessmentIcon />}
                  onClick={handleCheckStorageHealth}
                >
                  Check Storage Usage
                </Button>
              </Box>

              {/* Recent Silent Backups (last 3 captured automatically) */}
              <Box sx={{ mt: 1 }}>
                <Typography variant="subtitle2" sx={{ color: 'text.secondary', mb: 1 }}>
                  Recent Silent Backups (last {MAX_HISTORY})
                </Typography>
                {getRecentSilentBackups().length ? (
                  <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                    {getRecentSilentBackups().map(({ ts, dateLabel }) => (
                      <Button
                        key={ts}
                        size="small"
                        variant="text"
                        startIcon={<CloudDownloadIcon fontSize="small" />}
                        onClick={() => downloadSilentBackupByTimestamp(ts)}
                        sx={{ textTransform: 'none' }}
                        title={`Download backup from ${dateLabel}`}
                      >
                        {dateLabel}
                      </Button>
                    ))}
                  </Stack>
                ) : (
                  <Typography variant="body2" color="text.disabled">
                    No silent backup history yet. A snapshot will be captured automatically when you open this app.
                  </Typography>
                )}
              </Box>

              <Divider sx={{ my: 3 }} />

              {/* Maintenance Section */}
              <Box sx={{
                p: 3,
                backgroundColor: "#fff5f5", // Light red background for maintenance
                borderRadius: '12px',
                border: '1px solid #ffcdd2',
                borderLeft: '4px solid #f44336', // Red accent for maintenance
                mb: 3
              }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <Box
                    sx={{
                      width: 24,
                      height: 24,
                      borderRadius: '6px',
                      backgroundColor: '#f44336',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      mr: 1.5
                    }}
                  >
                    <DeleteSweepIcon sx={{ color: 'white', fontSize: '0.75rem' }} />
                  </Box>
                  <Typography
                    variant="subtitle1"
                    sx={{
                      fontFamily: "'Segoe UI', Roboto, 'Open Sans', sans-serif",
                      fontSize: "1.1rem",
                      fontWeight: 600,
                      color: "#d32f2f",
                      letterSpacing: "0.01em"
                    }}
                  >
                    Storage Maintenance
                  </Typography>
                </Box>

                <Typography variant="body2" color="text.secondary" gutterBottom sx={{ mb: 2 }}>
                  These actions help manage browser storage space when it gets full.
                </Typography>

                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                  <Button
                    variant="outlined"
                    color="warning"
                    startIcon={<DeleteSweepIcon />}
                    onClick={handlePurgeOldestBackup}
                  >
                    Remove Oldest Backup
                  </Button>

                  <Tooltip title="Warning: This will delete ALL stored backups for all apps">
                    <Button
                      variant="contained"
                      color="error"
                      startIcon={<DeleteForeverIcon />}
                      onClick={handlePurgeAllBackups}
                      sx={{
                        py: 1,
                        borderRadius: 2
                      }}
                    >
                      Purge All Backups
                    </Button>
                  </Tooltip>
                </Box>
              </Box>
            </Box>
          </AccordionDetails>
        </Accordion>
        
        {/* About Backup & Restore */}
        <Accordion>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography 
              variant="subtitle1"
              sx={{ 
                fontFamily: "'Segoe UI', Roboto, 'Open Sans', sans-serif",
                fontWeight: 500,
                display: 'flex',
                alignItems: 'center'
              }}
            >
              <InfoOutlinedIcon sx={{ mr: 1, fontSize: "1.1rem", color: 'info.main' }} />
              About Backup &amp; Restore
            </Typography>
          </AccordionSummary>

          <AccordionDetails>
            <Box sx={{ p: 2, backgroundColor: "#f5f5ff", borderRadius: 1 }}>
              <Typography variant="body1" sx={{ mb: 2 }}>
                The backup and restore feature allows you to save and transfer application configurations between environments.
              </Typography>
              
              {/* Visual explanation */}
              <Box sx={{ 
                p: 3, 
                backgroundColor: '#fafbff', 
                border: '1px solid #e8eaf6', 
                borderLeft: '4px solid #3f51b5', // Indigo accent
                borderRadius: '8px',
                mb: 2
              }}>
                <Typography variant="subtitle2" sx={{ color: '#283593', mb: 1, fontWeight: 600 }}>
                  How Backups Work:
                </Typography>
                <Typography variant="body2" sx={{ color: '#424242' }}>
                  • <strong>Manual Backup</strong>: Creates a JSON file with your app's complete configuration<br />
                  • <strong>Silent Backup</strong>: Automatically stored in your browser when you create a manual backup<br />
                  • <strong>Restore</strong>: Loads a backup file but won't update Entra ID until you click "Apply Changes"<br />
                  • <strong>Browser Storage</strong>: Used to temporarily store backups between sessions (limited to ~5MB total)
                </Typography>
              </Box>

              <Box sx={{
                p: 3,
                backgroundColor: '#fff8e1',
                border: '1px solid #ffb74d',
                borderLeft: '4px solid #ff9800', // Warning accent
                borderRadius: '8px'
              }}>
                <Typography variant="subtitle2" sx={{ color: '#e65100', mb: 1, fontWeight: 600 }}>
                  Important Notes:
                </Typography>
                <Typography variant="body2" sx={{ color: '#424242' }}>
                  • Backups contain sensitive information and should be stored securely<br />
                  • When restoring, any changes must be explicitly applied using the "Apply Changes" button<br />
                  • Browser storage has limited capacity and is cleared when you clear browser data<br />
                  • Always download important backups to your local device for safekeeping
                </Typography>
              </Box>
            </Box>
          </AccordionDetails>
        </Accordion>
      </CardContent>
    </Card>
  );
};

export default BackupAndRestore;